/**
 * `canvas` namespace catalog (ADR 0310 Phase A) — the canvas editor FRAMEWORK's
 * strings (`src/canvas/`): shell chrome, save/conflict, palette/outline/
 * properties panels, arrange + announce vocabulary, version history, device
 * preview. Moved VERBATIM from the app-builder namespace at extraction time.
 * Type-vocabulary strings (screens vs slides vs pages, delete-doc wording,
 * palette categories) stay in each canvas type's own namespace — see the fixed
 * type-key contract in `src/canvas/types.ts`.
 */
export const messages = {
  // CPKU-2 — accessible Yes/No name for pack boolean fields (the ✓/— glyph is aria-hidden).
  boolYes: 'Yes',
  boolNo: 'No',
  orgAmbiguousBody: "This link doesn't say which workspace this canvas belongs to, and you're in more than one. Pick the workspace to open it in.",
  orgNotMemberBody: "This link points at a workspace you're not a member of.",
  orgNoneBody: "You're not a member of any workspace yet, so there's nothing to open this canvas in.",
  histLoadFailedTitle: "Could not load version history",
  histLoadFailedBody: "This is NOT a report that no versions exist — the history could not be read.",
  loading: 'Loading editor…',
  loadError: 'Could not load this canvas.',
  notAvailable: 'This canvas isn’t available — it may have been deleted, belong to another workspace, or its feature may be disabled.',
  invalidArtifact: 'This design can’t be opened in the editor: it uses components or values outside the catalog. Ask the assistant to regenerate it.',
  invalidArtifactDetail: 'What was refused',
  noOrg: 'No workspace available.',
  retry: 'Retry',
  liveConnecting: 'Starting the live session…',
  liveConnectFailed: 'Couldn’t start the live session',
  liveConnectFailedBody: 'The collaboration service didn’t respond. Your document is safe — retry, or reopen it later to edit solo.',
  version: 'v{{n}}',
  unsaved: 'Unsaved',
  liveLocked: 'Unavailable during a live collaboration session',
  viewOnly: 'You can view this, but not edit it. Ask someone with edit access to make changes.',
  noWriteAccess: 'You don\'t have permission to change this.',
  live: 'Live',
  reconnecting: 'Reconnecting…',
  liveCollaborators: 'Live session — editing with {{names}}',
  reconnectingCollaborators: 'Reconnecting — session with {{names}}',
  annCollabDropped: 'Connection lost — reconnecting. Your edits will sync when it returns.',
  annCollabReconnected: 'Reconnected — your edits are syncing again.',
  guestN: 'Guest {{id}}',
  selectedBy: 'Selected by {{name}}',
  annPeerJoined: '{{name}} joined the live session',
  annPeersJoined: '{{n}} people joined the live session',
  annPeerLeft: '{{name}} left the live session',
  annPeersLeft: '{{n}} people left the live session',
  saving: 'Saving…',
  save: 'Save',
  saveError: 'Could not save. Please try again.',
  savedWithWarnings: 'Saved with {{n}} reference warnings: {{first}}',
  conflict: 'This canvas changed elsewhere — reload to get the latest version before saving.',
  reloadLatest: 'Reload latest',
  reloadLatestTitle: 'Discard your local edits?',
  reloadLatestBody: 'Reloading fetches the latest saved version and discards the edits you made since your last save.',
  palette: 'Components',
  paletteFilter: 'Filter components…',
  container: 'container',
  templates: 'Templates',
  outline: 'Outline',
  properties: 'Properties',
  deleteComponent: 'Delete component',
  duplicateComponent: 'Duplicate component',
  selectHint: 'Click a component in the preview or outline to edit its properties.',
  reorderHint: 'Press Alt with the Up or Down arrow to move the selected item among its siblings.',
  preview: 'Preview',
  previewUnsavedHint: 'Save your changes to preview them.',
  backToEditor: 'Back to editor',
  share: 'Share',
  sharing: 'Sharing…',
  shareCreated: 'Public link copied: {{url}}',
  shareCreatedNotCopied: 'Public link created — copying it failed, so copy it now: {{url}}',
  shareFailed: 'Could not create the share link.',
  history: 'History',
  historyTools: 'History',
  undo: 'Undo',
  redo: 'Redo',
  arrange: 'Arrange',
  moveUp: 'Move up',
  moveDown: 'Move down',
  moveOut: 'Move out of container',
  moveIn: 'Move into container',
  moveInHint: 'Move into {{container}}',
  moveLeft: 'Move left',
  moveRight: 'Move right',
  annAdded: '{{type}} added',
  annAddedInto: '{{type}} added into the selected container',
  annDeleted: '{{type}} deleted',
  annDuplicated: 'Component duplicated',
  annMoved_up: 'Moved up',
  annMoved_down: 'Moved down',
  annMoved_out: 'Moved out of its container',
  annMoved_in: 'Moved into {{container}}',
  annUndid: 'Undone',
  annRedid: 'Redone',
  hexAria: '{{field}} (6-digit hex color)',
  hexPlaceholder: '6-digit hex color, e.g. a leading hash + 2563EB',
  histEmpty: 'No versions captured yet — versions appear as you save.',
  histLoadError: 'Could not load version history.',
  histCompare: 'What changed?',
  histRestore: 'Restore',
  histRestoreTitle: 'Restore version {{n}}?',
  histRestoreBody: 'The restored design becomes a NEW version — nothing is lost, and you can restore forward again.',
  histRestoreFailed: 'Could not restore this version.',
  histRestored: 'Version restored.',
  histUnknownUser: 'Unknown user',
  device: 'Device',
  device_phone: 'Phone (390)',
  device_phoneLg: 'Phone large (430)',
  device_tablet: 'Tablet (834)',
  device_tabletLg: 'Tablet large (1024)',
  device_desktop: 'Desktop (1280)',
  device_full: 'Responsive (full width)',
  themePreview: 'Preview theme',
  theme_light: 'Light',
  theme_dark: 'Dark',
  fullscreen: 'Fullscreen',
  exitFullscreen: 'Exit fullscreen',
  autoplayPlay: "Play the deck (advances every few seconds)",
  autoplayPause: "Pause autoplay",
  deleteElement: 'Delete element',
  selectElementHint: 'Select an element from the list to edit it.',
  emptyCollection: 'Nothing here yet — add one from the palette.',
  back: 'Back',
  workspaceModes: 'Workspace modes',
  workspaceStatus: 'Canvas status',

  // Type-gallery vocabulary (ADR 0314) — display names for picking a canvas
  // TYPE (the Documents creation gallery, pickers); editor vocabulary stays in
  // each type's own namespace.
  type_slides: 'Slide deck',
  typeHint_slides: 'Slides you present — title, bullets, quotes, images.',
  type_drawing: 'Drawing',
  typeHint_drawing: 'Shapes, lines, and text on a freeform canvas.',
  type_cad: 'CAD model',
  typeHint_cad: 'Solids with real dimensions — boxes, cylinders, spheres.',
  type_campaign: 'Campaign plan',
  typeHint_campaign: 'Channels, funnel stages, and assets for a campaign.',
  type_app: 'App design',
  typeHint_app: 'Screens and components for an app prototype.',
  type_document: 'Document',
  typeHint_document: 'A rich-text document — headings, styled text, and lists.',
  // ADR 0323 — the graph / screen-flow surface. `{label}`/`{from}`/`{to}` are
  // filled by GraphSurface (a plain component, not a t() consumer).
  graphViewGraph: 'Graph',
  graphViewEdit: 'Edit',
  graphSurface: 'Screen-flow graph',
  graphConnectFrom: 'Connect from {label}',
  graphConnectTo: 'Connect to {label}',
  graphCancelConnect: 'Cancel',
  graphConnectArmed: 'Connecting from {label} — pick a target',
  graphConnected: 'Connected {from} to {to}',
  graphDeletedEdge: 'Connection deleted',
  graphHome: 'Start',
  graphEmpty: "No screens yet — add one to start the flow.",
  graphEdgeHeading: "Connection: {{from}} → {{to}}",
  graphDeleteEdge: "Delete connection",
  graphAddConnected: "Add connected screen from {label}",
  copyComponent: "Copy",
  pasteComponent: "Paste",
  pasteComponentHint: "Paste the copied {{type}} here",
  annCopied: "Copied {{type}}",
  annPasted: "Pasted {{type}}",
  templatesBrowse: "Browse templates",
  templatesGalleryTitle: "Screen templates",
  catFavorites: "Favorites",
  catRecent: "Recently used",
  favoriteAdd: "Add {{label}} to favorites",
  favoriteRemove: "Remove {{label}} from favorites",
  graphEdgeSelected: "Connection {from} → {to} selected",
  versionFrameAddedSince: "Added “{{name}}”",
  versionFrameRemovedSince: "Removed “{{name}}”",
  versionFrameRenamed: "Renamed “{{from}}” → “{{to}}”",
  versionFrameCount: "{{from}} → {{to}} slides",
  versionNoFrameChanges: "No structural changes",
  versionFieldsChanged: "Changed: {{fields}}",
  versionNoChanges: "No changes",
  // ADR 0328 P4 — present mode + the phone remote.
  presentStage: "Presentation",
  frameCounter: "{{n}} / {{total}}",
  sharedDeckEmptyTitle: 'Nothing to show yet',
  sharedDeckEmptyBody: 'This deck has no visible slides right now. Check back later — the owner may still be working on it.',
  exitPresent: "Exit",
  elapsed: "Elapsed time",
  nextFrame: "Next",
  prevFrame: "Previous",
  endOfDeck: "End of deck",
  speakerNotes: "Speaker notes",
  noNotes: "No notes for this slide.",
  blankBlack: "Black screen",
  blankWhite: "White screen",
  openAudienceWindow: "Audience window",
  phoneRemote: "Phone remote",
  remoteActive: "Remote active",
  remoteFailed: "Could not start the phone remote.",
  scanToJoin: "Scan with your phone to control the show.",
  copyJoinLink: "Copy join link",
  linkCopied: "Join link copied.",
  jumpGrid: "Jump to a frame",
  frameN: "Frame {{n}}",
  remoteExpired: "This remote link has expired. Ask the presenter for a new code.",
  remoteConnected: "Connected",
  remoteReconnecting: "Reconnecting\u2026",
  copyFrame: "Copy to clipboard",
  pasteFrame: "Paste from clipboard",
  annFrameCopied: "\"{{name}}\" copied",
  annFramePasted: "Slide pasted",
  presenterView: "Presenter view",
  presentEmpty: "Nothing to present",
  presentEmptyBody: "This canvas has no frames yet \u2014 add some in the editor first.",
  screenBlanked: "Screen blanked",
  zoomControls: "Zoom",
  zoomIn: "Zoom in",
  zoomOut: "Zoom out",
  zoomReset: "Zoom {{pct}}% \u2014 reset to fit",
  toolModes: "Tools",
  toolSelect: "Select",
  annTool: "{{tool}} tool",
  annToolSelect: "Select tool",
  viewportLabel: "Canvas viewport \u2014 arrow keys pan",
  zoomToSelection: "Zoom to selection",
  selectionPill: "Selection actions",
  notesDrawerLabel: "Speaker notes",
  notesDrawerTitle: "Notes",
  notesDrawerEmpty: "No notes for this frame yet",
  notesDrawerPlaceholder: "Notes only you see while presenting\u2026",
  annNotesSaved: "Notes saved",
  quickPropsLabel: "Quick formatting",
  selChipCount: "{{count}} selected",
  selChipOne: "1 selected",
  zoomToFit: "Zoom to fit",
  collapsePalette: "Collapse the left rail",
  expandPalette: "Expand the left rail",
  collapseProps: "Collapse the properties rail",
  expandProps: "Expand the properties rail",
  resizePalette: "Resize the left rail",
  resizeProps: "Resize the properties rail",
  annPaletteCollapsed: "Left rail collapsed",
  annPaletteExpanded: "Left rail expanded",
  annPropsCollapsed: "Properties rail collapsed",
  annPropsExpanded: "Properties rail expanded",
  shortcutToggleLeftRail: "Toggle the left rail",
  shortcutToggleRightRail: "Toggle the properties rail",
  moreActions: "More actions",
  editorCommandsGroup: "Canvas editor",
  zoom100: "Zoom to 100%",
  zoomMenu: "Zoom {{pct}}% \u2014 presets & fit",
  shortcutsTitle: "Keyboard shortcuts",
  shortcutGroup_general: "General",
  shortcutGroup_arrange: "Arrange",
  shortcutGroup_view: "View",
  shortcutGroup_type: "This canvas",
  shortcutEsc: "Back to Select / clear selection",
  shortcutDuplicate: "Duplicate",
  bringForward: "Bring forward",
  sendBackward: "Send backward",
  bringToFront: "Bring to front",
  sendToBack: "Send to back",
  annReordered_forward: "Brought forward",
  annReordered_backward: "Sent backward",
  annReordered_front: "Brought to front",
  annReordered_back: "Sent to back",
  lockElement: "Lock",
  unlockElement: "Unlock",
  hideElement: "Hide",
  showElement: "Show",
  groupSelection: "Group",
  ungroupSelection: "Ungroup",
  annGrouped: "Grouped {{count}} elements",
  annUngrouped: "Ungrouped",
  elementName: "Name",
  annElementsAdded: "{{count}} added",
  align: "Align",
  align_left: "Left",
  align_centerH: "Center",
  align_right: "Right",
  align_top: "Top",
  align_middle: "Middle",
  align_bottom: "Bottom",
  align_distributeH: "Distribute \u2194",
  align_distributeV: "Distribute \u2195",
  annAligned: "Arranged",
  frameAnnounce: "Frame {{n}} of {{total}}: {{name}}",
  presentOutline: "Deck outline",
  presentOutlineItem: "Frame {{n}}: {{name}}",
  presentOutlineCurrent: "{{label}} (current)",
  presentKeysHint: "\u2190 \u2192 navigate \u00b7 B/W blank \u00b7 Esc exit",
  remoteStarting: "Starting\u2026",
  qrUnavailable: "QR code unavailable \u2014 use the join link instead.",
  copyShareLink: "Copy share link",
  annElementsDeleted: "{{count}} deleted",
  screensRail: "Screens",
  annCapReached: "At the {{max}}-item limit",
  shortcutDelete: "Delete selection",
  annAlignedOp: "{{op}}",
  kioskAuto: "Auto · {{s}}s",
  kioskPaused: "Paused",
  presenterTitle: "{{title}} — presenter",
  graphMinimap: "Overview map \u2014 click to pan, Enter to fit",
  graphDeviceFrame: "Device frame",
  annGestureCancelled: "Cancelled",
  cutComponent: "Cut",
  copyStyle: "Copy style",
  pasteStyle: "Paste style",
  pasteStyleHint: "Apply the style copied from {{type}}",
  annCut: "Cut {{type}}",
  annStyleCopied: "Copied {{n}} style values from {{type}}",
  annStylePasted: "Applied {{n}} style values",
  annStyleNone: "No copied style applies to this component",
  nodeFlags: "Visibility & lock",
  nodeHidden: "Hidden",
  nodeLocked: "Locked",
  annNodeHidden: "Component hidden",
  annNodeShown: "Component shown",
  annNodeLocked: "Component locked",
  annNodeUnlocked: "Component unlocked",
  annChildNotAllowed: "{{container}} can't contain that component here",
  diff_added: "Added",
  diff_removed: "Removed",
  diff_changed: "Changed",
  versionFieldChanged: "\u2018{{field}}\u2019 changed",
  shareDisclosureTitle: "Share publicly?",
  shareConfirm: "Create link",
  previewModalClose: "Close dialog",
  previewDiagnostics: "Preview diagnostics",
  previewTraceEmpty: "No preview activity yet — tap an action in the prototype.",
  simMode_ok: "OK",
  simMode_error: "Error",
  simMode_empty: "Empty",
  kitsTitle: "Kits",
  kitTag: "Kit",
  kitInsert: "Insert {{n}} screens",
  kitCancel: "Cancel",
  annKitInserted: "Inserted the {{kit}} kit",
  annKitRejected: "The {{kit}} kit doesn't fit in this document",
};
